import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import qs from 'qs';
import { Box, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from 'theme';
import { ActionsButton, Datatable, Loader, ScoreChangeIndicator } from 'ui';
import { ChipButton, LinkButton } from 'ui/Buttons';
import { titleByRatingType } from 'shared/constants/titleByRatingType';
import { usePortfolioModals, useQuery, useSelectedTableRows, useTable } from 'shared/hooks';
import { SelectedEntities } from 'shared/types/portfolios';
import { activityMappingPortfolioItems } from 'shared/mappings/activityLabels';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { useDispatch, useSelector } from 'react-redux';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { exportEntities } from 'services/export/exportService';
import { ESGScoringToolModal } from 'modules/ESG';
import { lastVisitedRatingTypeAction } from 'store/shared/lastVisited/lastVisitedActions';
import { MUIDataTableMeta } from 'mui-datatables';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { getCofaceRequestsAllowed } from 'selectors/creditReviewSelectors';
import { useCofaceContract } from 'shared/hooks/useCofaceContract/useCofaceContract';
import { parseRatingType, selectedItemsToProductsParser } from 'shared/parsers';
import { entityPortfolioModuleData } from 'shared/functions/entityPortfolioModuleData';
import { creditReviewRequestTooltips } from 'shared/constants/creditReview/creditReviewRequestTooltips';
import { RequestConfirmationModal as CreditReviewRequestConfirmationModal } from 'modules/creditReview/CreditReviewModals';
import { requestSigEntityRatingAction } from 'store/entity/entityActions';
import { useConfirmedRequest } from 'shared/hooks/useConfirmedRequest/useConfirmedRequest';
import { orderTypes } from 'shared/constants/orderTypes';
import { purchaseTypes } from 'shared/constants/purchaseTypes';
import { postOrderMany } from 'services/orders/ordersService';
import { companyEntityModules } from 'shared/constants/company';
import { useGetSentifyRequests } from 'modules/ESGSentiment/services/esgSentiment';
import { requestSentimentScore } from 'services/esg/fetchSentimentSummary';
import { StyledSwitch } from '../../../../ui';
import {
  useDeleteEntities,
  useFetchPortfolioItems,
  useToggleSigMonitoring,
} from '../../GraphQL/portfolios/hooks';
import { makePortfolioItemsTableData } from '../utils';
import { UpdateEntitiesModal } from '../../Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { CreateNewPortfolioModal, RequestEsgScoreModal } from '../../Modals';
import {
  AuthData,
  AuthStore,
  CollectionType,
  EntityEssential,
  EntityInitialFiltersData,
  EsgStore,
  FiltersData,
  PortfolioAction,
  PortfolioEntity,
  PortfolioItemsProps,
  PortfolioType,
  RequestTab,
  SigMonitor,
  SigProduct,
  SubScore,
} from '../types';
import { ScoreLock } from './components/ScoreLock';
import { EntityScore } from './components/EntityScore';
import { DatatableEntityFilters } from './components/DatatableEntityFilters';
import { fetchInitialFiltersData } from './services/filtersService';
import { DatatableChipFilters } from './components/DatatableChipFilters';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  nameButton: {
    minWidth: 'unset',
  },
  chipCustomScore: {
    color: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.darkGrayAlt,
  },
  tooltip: {
    marginLeft: 3,
  },
  buttonLink: {
    cursor: 'pointer',
  },
  availableWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  availableCounter: {
    fontWeight: 500,
  },
  requestsAvailableLockIcon: {
    fontSize: 20,
  },
  counterLoader: {
    margin: theme.spacing(1, 2, 1, 1),
  },
  cstSelect: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),

    '& > .MuiSelect-select': {
      fontWeight: 500,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
    },
  },
  cstLabel: {
    gridGap: theme.spacing(1),
  },
  infoIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    color: theme.palette.primary.main,
  },
}));

type PortfolioEntitiesParams = {
  ratingType?: ratingTypes;
  activeTab?: string;
};

export const PortfolioEntities: FC<PortfolioItemsProps> = ({
  portfolioId,
  total = 0,
  refetchPortfolio = null,
  portfolioData,
  totalCounter,
}) => {
  const lastVisitedRatingType = useSelector<any>(
    ({ shared }) => shared.lastVisited.ratingType,
  ) as ratingTypes;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { error, success } = useSnackbar();
  const dispatch = useDispatch();
  const classes = useStyles();
  const queryParams = useQuery() as PortfolioEntitiesParams;

  const [requestConfirmationModalOpen, setRequestConfirmationModalOpen] = useState(false);

  const toggleRequestConfirmationModal = () => {
    setRequestConfirmationModalOpen(!requestConfirmationModalOpen);
  };

  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
    clearSingle,
  } = useSelectedTableRows();

  const toggleSigMonitor = useToggleSigMonitoring();

  const [variant, setVariant] = useState<PortfolioAction>();
  const [updating, setUpdating] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });

  const { createPortfolio, addToPortfolio, requestEsgScore, createCompanies } =
    usePortfolioModals();

  const deleteEntities = useDeleteEntities();
  const [activeItem, setActiveItem] = useState<string>();
  const [requestSelectedESG, setRequestSelectedESG] = useState<EntityEssential[]>();
  const { cofaceAccessType, isPending: cofaceContractInfoPending } = useCofaceContract();
  const cofaceRequestsAllowed = useSelector(getCofaceRequestsAllowed);
  const requestButtonTooltipTitle = creditReviewRequestTooltips[cofaceAccessType];
  const sigProduct = useSelector<AuthStore, AuthData>(({ auth }) => auth.data);
  const esgRequestPermitted = useSelector<EsgStore, boolean>(
    ({ esgContract: { esgRequestPermission } }) => esgRequestPermission,
  );
  const maxRequestCount: number = 25;
  const [entitiesToRequestCount, setEntitiesToRequestCount] = useState<number>(1);
  const [cstScenario, setCstScenario] = useState('orderly');
  const { data: sentifyRequests, loading: loadingSentify } = useGetSentifyRequests();

  const { isDedicated, portfolioType } = portfolioData;

  const isRequestHistory = portfolioType === PortfolioType.REQUEST_HISTORY;
  const isRSRPortfolio = [
    PortfolioType.RSR_PORTFOLIO,
    PortfolioType.PRIVATE_RATING_PORTFOLIO,
    PortfolioType.ON_DEMAND_RATING_PORTFOLIO,
  ].includes(portfolioType);

  const { ratingType = isRequestHistory ? ratingTypes.CREDIT_RATING : undefined, activeTab } =
    queryParams;

  const features = useFeatures();
  const [filtersData, setFiltersData] = useState<FiltersData>({});
  const [filterFieldsInitialData, setFilterFieldsInitialData] =
    useState<EntityInitialFiltersData>();

  const requestedTab = (): RequestTab => {
    if (isRequestHistory) {
      if (ratingType === ratingTypes.ESG_IMPACT) {
        return 'ESG';
      }
      if (features.creditReview && ratingType === ratingTypes.CREDIT_REVIEW) {
        return 'CREDIT_REVIEW';
      }
      if (ratingType === ratingTypes.SENTIMENT) {
        return 'ESG_SENTIMENT';
      }
    }
    return 'OVERVIEW';
  };

  // eslint-disable-next-line consistent-return
  const handleRequestRating = () => {
    const entitiesAvailableToRequest = generateEntitiesAvailableToRequest();
    if (!entitiesAvailableToRequest.length) {
      return error(t('PORTFOLIO.UNABLE_TO_REQUEST_SELECTED'));
    }
    // eslint-disable-next-line default-case
    switch (ratingType) {
      case ratingTypes.CREDIT_RATING:
        {
          setUpdating(true);
          const filteredRatingRequest = entitiesAvailableToRequest.map((entity) => ({
            id: entity.artifactId,
            level: 'L4',
          }));
          const products = selectedItemsToProductsParser({
            selected: filteredRatingRequest,
            artifactType: orderTypes.ENTITY,
            frequency: 0,
            purchaseType: purchaseTypes.ONE_TIME,
          });

          postOrderMany({ products }).finally(() => {
            refreshList(true, refreshReasons.PRODUCT_REQUESTED);
            setUpdating(false);
          });
        }
        break;
      case ratingTypes.CREDIT_REVIEW:
        {
          const ids: any = entitiesAvailableToRequest.map((entity) => entity.artifactId);
          const sampleProduct: SigProduct = entitiesAvailableToRequest[0].product! as SigProduct;
          const { organisationId, userId } = sampleProduct;
          dispatch(requestSigEntityRatingAction({ ids, organisationId, userId }));
          setEntitiesToRequestCount(1);
          refreshList(true, '');
        }
        break;
      case ratingTypes.ESG_IMPACT:
        setRequestSelectedESG(entitiesAvailableToRequest);
        requestEsgScore.show();
        break;
    }
  };

  const { scheduleRequest } = useConfirmedRequest({
    handleRequest: handleRequestRating,
    confirmationModalOpen: toggleRequestConfirmationModal,
  });

  const generateEntitiesAvailableToRequest = () => {
    if (!ratingType) {
      return [];
    }
    const entities: EntityEssential[] = [];

    const selectedEntities: PortfolioEntity[] = Object.values(currentSelect.itemIds).map(
      (itemId) => {
        return portfolioItems.find((entity: PortfolioEntity) => entity.id === itemId)!;
      },
    );

    selectedEntities.forEach((entity: PortfolioEntity) => {
      const { canRequest, requested, product, orderType } = entityPortfolioModuleData(
        entity,
        sigProduct,
        cofaceAccessType,
        esgRequestPermitted,
      )[ratingType];

      const entityEssential: EntityEssential = {
        artifactId: entity.artifactId,
        product,
        orderType,
      };
      if (canRequest && !requested) entities.push(entityEssential);
    });
    setEntitiesToRequestCount(entities.length);
    return entities;
  };

  useEffect(() => {
    if (ratingType === ratingTypes.CREDIT_REVIEW && !features.creditReview) {
      onCompanyModuleChange(ratingTypes.CREDIT_RATING);
      return;
    }

    if (ratingType && ratingType !== lastVisitedRatingType) {
      dispatch(lastVisitedRatingTypeAction({ ratingType }));
    }
  }, [ratingType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshList(true, refreshReasons.UPDATED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !!portfolioType &&
      [PortfolioType.REFERENCE_PORTFOLIO_EUROPE, PortfolioType.REFERENCE_PORTFOLIO_GLOBAL].includes(
        portfolioType,
      ) &&
      activeTab === 'entities'
    ) {
      onCompanyModuleChange(ratingTypes.CREDIT_RATING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const toggleSig = (artifactId: string) => {
    setUpdating(true);
    toggleSigMonitor({ portfolioId, artifactId })
      .then(({ data }) => {
        const message = (data as unknown as SigMonitor).toggleSigMonitoring.message || t('Success');
        refreshList(true, refreshReasons.UPDATED);
        success(message);
      })
      .catch(() => {
        error(t('Unable to change renewal value.'));
      })
      .finally(() => setUpdating(false));
  };

  const deleteEntity = () => {
    setUpdating(true);
    deleteEntities({
      portfolioItemsIds: selectedAll ? [] : Object.values(currentSelect.itemIds),
      portfolioId,
      selectedAll,
    })
      .then(({ data }) => {
        const message =
          (data as unknown as { deletePortfolioItems: any }).deletePortfolioItems?.message ||
          t('Success');
        refreshList(true, refreshReasons.DELETED);
        success(message);
      })
      .catch(() => {
        error(t('Unable to delete entities.'));
      })
      .finally(() => setUpdating(false));
  };

  const copyEntity = () => {
    setVariant(selectedAll ? 'copy_all' : 'copy');
    addToPortfolio.toggle();
  };

  const moveEntity = () => {
    setVariant(selectedAll ? 'move_all' : 'move');
    addToPortfolio.toggle();
  };

  const handleRequestSelected = () => {
    if (portfolioItems.length) {
      if (ratingType === ratingTypes.CREDIT_REVIEW) {
        scheduleRequest();
      } else {
        handleRequestRating();
      }
    }
  };

  const exportToExcel = () => {
    const items = Object.values(currentSelect.itemIds);

    // eslint-disable-next-line no-shadow
    const onError = (err: string) => error(err);
    const onSuccess = (message: string) => success(t('TOOLBAR.EXPORT_STARTED'));

    exportEntities({
      items: selectedAll ? [] : items,
      onError,
      onSuccess,
      portfolioId,
      selectedAll,
      ratingType,
    });
    clearSingle();
  };

  const exportFullDataToExcel = () => {
    const items = Object.values(currentSelect.itemIds);

    // eslint-disable-next-line no-shadow
    const onError = (err: string) => error(err);
    const onSuccess = (message: string) => success(t('TOOLBAR.EXPORT_STARTED'));

    exportEntities({
      items: selectedAll ? [] : items,
      onError,
      onSuccess,
      portfolioId,
      selectedAll,
      ratingType,
      exportMode: 'extended',
    });
    clearSingle();
  };

  const onCompanyModuleChange = (value?: ratingTypes) => {
    if (ratingType !== value) {
      navigate({
        pathname: location.pathname,
        search: qs.stringify({ ...queryParams, ratingType: value }, { addQueryPrefix: true }),
      });
    }
  };

  const prepareRequestIds = () => {
    if (!activeItem && requestSelectedESG) {
      return requestSelectedESG.map((esg) => esg.artifactId);
    }
    if (activeItem) {
      return [activeItem];
    }
    return Object.values(currentSelect.items).map((item) => (item as Array<any>)[0][1]);
  };

  /* ******************************************************************** */
  /*                         Datatable filters                            */
  /* ******************************************************************** */

  useEffect(() => {
    if (features.popoverFiltersPortfolioManager) {
      fetchInitialFiltersData(portfolioId, CollectionType.PORTFOLIO_ITEMS).then((response) =>
        setFilterFieldsInitialData(response),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltersSubmit = (data: FiltersData) => {
    const dataToSet: FiltersData = data;
    if (dataToSet.countryCode) {
      const countryCodeArray = dataToSet.countryCode.map((country: any) => country.iso);
      dataToSet.countryCode = countryCodeArray;
    }
    if (dataToSet.industry) {
      const industryConverted = dataToSet.industry.map((industry: any) => industry.value);
      dataToSet.industry = industryConverted;
    }
    setFiltersData(dataToSet);
  };

  const handleDeleteFilter = (propertyName: keyof FiltersData) => {
    // eslint-disable-next-line camelcase
    if (filtersData) {
      // eslint-disable-next-line camelcase
      const { [propertyName]: _removedProperty, ...filtersEdited } = filtersData!;
      setFiltersData(filtersEdited);
    }
  };

  const handleClearAllFilters = () => setFiltersData({});

  const handleCustomChipDelete = (propertyName: keyof FiltersData, value: string) => {
    if (filtersData && filtersData[propertyName]) {
      const valuesEdited = (filtersData[propertyName] as string[]).filter(
        (elementName) => elementName !== value,
      );
      setFiltersData((prevState) => ({
        ...prevState,
        [propertyName]: valuesEdited,
      }));
    }
  };

  const entitiesFilters = filterFieldsInitialData && (
    <DatatableEntityFilters
      filterFieldsInitialData={filterFieldsInitialData}
      prevFiltersValues={filtersData}
      handleSubmit={handleFiltersSubmit}
      ratingTypePage={ratingType}
    />
  );

  const filterChips = filtersData && (
    <DatatableChipFilters
      filtersData={filtersData}
      handleDelete={handleDeleteFilter}
      handleClearAll={handleClearAllFilters}
      handleCustomChipDelete={handleCustomChipDelete}
    />
  );

  const ratingTypesValues = Object.values(ratingTypes).filter((item) => {
    if (item === ratingTypes.FUNDS) return false;
    if (item === ratingTypes.CREDIT_REVIEW && !features.creditReview) return false;

    return true;
  });

  const requestHistoryRatingTabs = [
    features.creditReview && ratingTypes.CREDIT_REVIEW,
    ratingTypes.ESG_IMPACT,
    ratingTypes.SENTIMENT,
  ].filter(Boolean);

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  const tabsButtons = (
    <Box display="flex" py={2} px={1} minHeight={32}>
      {(isRequestHistory
        ? requestHistoryRatingTabs
        : [
            undefined,
            ...ratingTypesValues.filter((item) => {
              if (item === ratingTypes.SENTIMENT) {
                return features.esgSentiment;
              }

              return true;
            }),
          ]
      ).map((ratingTypeValue) => {
        const isActive = ratingTypeValue === ratingType;
        return (
          <ChipButton
            key={ratingTypeValue ?? 'overview'}
            isActive={isActive}
            onClick={() => onCompanyModuleChange(ratingTypeValue)}
            style={{ whiteSpace: 'nowrap' }}
          >
            {!ratingTypeValue ? t('PORTFOLIO.OVERVIEW') : titleByRatingType[ratingTypeValue]}
          </ChipButton>
        );
      })}
    </Box>
  );

  const availableRequestsRender = cofaceContractInfoPending ? (
    <Loader classes={classes.counterLoader} size={20} />
  ) : (
    <div className={classes.availableWrapper}>
      <h4>{t('CREDIT_REVIEW.CREDIT_REVIEW_REQUESTS')}</h4>
      <Box
        display="flex"
        margin={1}
        marginRight={2}
        paddingY={0.5}
        paddingX={1.75}
        borderRadius="15px"
        bgcolor={theme.palette.custom.lightBlue}
      >
        {cofaceRequestsAllowed === 0 ? (
          <Tooltip title={requestButtonTooltipTitle} className={classes.requestsAvailableLockIcon}>
            <LockIcon />
          </Tooltip>
        ) : (
          <Typography color="primary" variant="body2" className={classes.availableCounter}>
            {cofaceRequestsAllowed}
          </Typography>
        )}
      </Box>
    </div>
  );

  const sentimentRequestsRender = loadingSentify ? (
    <Loader classes={classes.counterLoader} size={20} />
  ) : (
    <div className={classes.availableWrapper}>
      <h4>{t('ESG.SENTIMENT_REQUESTS')}</h4>
      <Box
        display="flex"
        margin={1}
        marginRight={2}
        paddingY={0.5}
        paddingX={1.75}
        borderRadius="15px"
        bgcolor={theme.palette.custom.lightBlue}
      >
        <Typography color="primary" variant="body2" className={classes.availableCounter}>
          {sentifyRequests?.requestLeft}
        </Typography>
      </Box>
    </div>
  );

  const nameRender = ([artifactId, name, module]: string[]) => {
    if (portfolioType === PortfolioType.SAMPLE_PORTFOLIO) return name;

    const moduleGroup = companyEntityModules.find((item) => item.value === module)?.module;
    const moduleParam = moduleGroup ? `&module=${moduleGroup}` : '';
    const companyModule = module === ratingTypes.CREDIT_REVIEW ? module : 'summary-card';
    const details = module === ratingTypes.CREDIT_REVIEW ? '/details' : '';

    return (
      <LinkButton
        to={`/entities/${artifactId}${details}?companyModule=${companyModule}${moduleParam}`}
        label={name?.toUpperCase()}
      />
    );
  };

  const activityRender =
    (module: ratingTypes) =>
    (
      value: (string | number)[] | null,
      { rowData: [[_id, _artifactId, entity]] }: MUIDataTableMeta,
    ) => {
      if (!value) {
        return '';
      }

      const [activity, oldScore, scoreDate] = value;

      if (activity === '' || activity === 'N/A') {
        return activity;
      }

      if (activity === 'lock') {
        return <ScoreLock entity={entity} module={module} />;
      }

      return (
        <ScoreChangeIndicator
          activity={activity as string}
          activityMapping={activityMappingPortfolioItems}
          scoreChangeDate={scoreDate as string}
          scoreOld={oldScore}
          showLabel
        />
      );
    };

  const ratingTypeRender = (ratingTypeInColumn: string | null) => {
    if (ratingTypeInColumn === 'N/A' || !ratingTypeInColumn) {
      return 'N/A';
    }
    return <Box>{parseRatingType(ratingTypeInColumn)}</Box>;
  };

  const actionRender = (_: unknown, { rowData: [[id, artifactId]] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id, artifactId)} actions={getActions(singleSelect)} />
  );

  const scoreRender = (module: ratingTypes, subscore?: SubScore) => (entity: PortfolioEntity) =>
    entity ? (
      <EntityScore
        entity={entity}
        module={module}
        subscore={subscore}
        onRequestEsg={() => {
          setActiveItem(entity.artifactId);
          requestEsgScore.show();
        }}
        onRequestSentiment={() =>
          requestSentimentScore(entity.artifactId).then(() =>
            refreshList(true, refreshReasons.UPDATED),
          )
        }
        onRequestCreditReview={() => setRequestConfirmationModalOpen(true)}
      />
    ) : null;

  const cstTooltipRenderer = ({ index, ...column }) => (
    <Tooltip key={index} placement="top" title={<>{t('Yearly Loss (Ave.) % - (2022-2050)')}</>}>
      <Box display="flex" alignItems="center" className={classes.cstLabel}>
        {column.label}
        <InfoIcon className={classes.infoIcon} />
      </Box>
    </Tooltip>
  );

  const sigMonitoringRender = (item) => {
    const {
      artifactId,
      meta: { sigMonitoring },
    } = item;
    return (
      <>
        <StyledSwitch
          defaultChecked={sigMonitoring === true}
          value={sigMonitoring}
          disabled={sigMonitoring === null}
          onChange={() => toggleSig(artifactId)}
        />
      </>
    );
  };

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'id',
        options: {
          display: false,
        },
      },
      {
        name: 'name',
        label: t('PORTFOLIO.AVAILABLE_ENTITIES'),
        options: {
          customBodyRender: nameRender,
        },
      },
      {
        name: 'dataSource',
        label: t('PORTFOLIO.DATA_SOURCE'),
        options: {
          customBodyRender: (label: string | null) => (label ? t(label) : null),
          display: (!isRequestHistory && !isNil(ratingType)) || isRSRPortfolio,
        },
      },
      {
        name: 'country',
        label: t('PORTFOLIO.COUNTRY'),
      },
      {
        name: 'city',
        label: t('PORTFOLIO.CITY'),
        options: {
          display: ratingType === ratingTypes.CREDIT_REVIEW,
        },
      },
      {
        name: 'postcode',
        label: t('PORTFOLIO.POSTCODE'),
        options: {
          display: ratingType === ratingTypes.CREDIT_REVIEW,
        },
      },
      {
        name: 'industry',
        label: t('PORTFOLIO.INDUSTRY'),
      },
      {
        name: 'creditRating',
        label: t('PORTFOLIO.CREDIT_RATING'),
        options: {
          customBodyRender: scoreRender(ratingTypes.CREDIT_RATING),
          display: !ratingType && !isRSRPortfolio,
        },
      },
      {
        name: 'creditReview',
        label: t('PORTFOLIO.CREDIT_REVIEW'),
        options: {
          customBodyRender: scoreRender(ratingTypes.CREDIT_REVIEW),
          display:
            !isRSRPortfolio &&
            (!ratingType || ratingType === ratingTypes.CREDIT_REVIEW) &&
            features.creditReview,
        },
      },
      {
        name: 'esgImpactReview',
        label: t('PORTFOLIO.ESG_IMPACT_REVIEW'),
        options: {
          customBodyRender: scoreRender(ratingTypes.ESG_IMPACT),
          display: !isRSRPortfolio && !ratingType,
        },
      },
      {
        name: 'ratingType',
        label: t('PORTFOLIO.RATING_TYPE'),
        options: {
          customBodyRender: ratingTypeRender,
          display: isRSRPortfolio || ratingType === ratingTypes.CREDIT_RATING,
        },
      },
      {
        name: 'demandType',
        label: t('PORTFOLIO.DEMAND_TYPE'),
        options: {
          sort: true,
          display: isRSRPortfolio || ratingType === ratingTypes.CREDIT_RATING,
        },
      },
      {
        name: 'entityRating',
        label: t('PORTFOLIO.ENTITY_RATING'),
        options: {
          customBodyRender: scoreRender(ratingTypes.CREDIT_RATING),
          display: isRSRPortfolio || ratingType === ratingTypes.CREDIT_RATING,
        },
      },
      {
        name: 'outlook',
        label: t('PORTFOLIO.OUTLOOK'),
        options: {
          display: isRSRPortfolio || ratingType === ratingTypes.CREDIT_RATING,
        },
      },
      {
        name: 'shortTermRating',
        label: t('PORTFOLIO.SORT_TERM_RATING'),
        options: {
          customBodyRender: scoreRender(ratingTypes.CREDIT_RATING, 'shortTermRating'),
          display: isRSRPortfolio || ratingType === ratingTypes.CREDIT_RATING,
        },
      },
      {
        name: 'probabilityOfDefault',
        label: t('PORTFOLIO.PROBABILITY_OF_DEFAULT'),
        options: {
          sort: false,
          display: ratingType === ratingTypes.CREDIT_REVIEW,
        },
      },
      {
        name: 'countryRisk',
        label: t('PORTFOLIO.COUNTRY_RISK'),
        options: {
          sort: false,
          display: ratingType === ratingTypes.CREDIT_REVIEW,
        },
      },
      {
        name: 'renewal',
        label: t('PORTFOLIO.RENEWAL'),
        options: {
          sort: true,
          display: isRequestHistory && ratingType === ratingTypes.CREDIT_REVIEW,
          customBodyRender: sigMonitoringRender,
        },
      },
      {
        name: 'esgEnvironment',
        label: t('PORTFOLIO.ENVIRONMENTAL'),
        options: {
          customBodyRender: scoreRender(ratingTypes.ESG_IMPACT, 'environmental'),
          display: ratingType === ratingTypes.ESG_IMPACT,
        },
      },
      {
        name: 'esgSocial',
        label: t('PORTFOLIO.SOCIAL'),
        options: {
          customBodyRender: scoreRender(ratingTypes.ESG_IMPACT, 'social'),
          display: ratingType === ratingTypes.ESG_IMPACT,
        },
      },
      {
        name: 'esgGovernance',
        label: t('PORTFOLIO.GOVERNANCE'),
        options: {
          customBodyRender: scoreRender(ratingTypes.ESG_IMPACT, 'governance'),
          display: ratingType === ratingTypes.ESG_IMPACT,
        },
      },
      {
        name: 'esgTotal',
        label: t('Total'),
        options: {
          customBodyRender: scoreRender(ratingTypes.ESG_IMPACT),
          display: ratingType === ratingTypes.ESG_IMPACT,
        },
      },
      {
        name: 'esgEnvironment',
        label: t('PORTFOLIO.ENVIRONMENTAL'),
        options: {
          customBodyRender: scoreRender(ratingTypes.SENTIMENT, 'environmental'),
          display: ratingType === ratingTypes.SENTIMENT,
        },
      },
      {
        name: 'esgSocial',
        label: t('PORTFOLIO.SOCIAL'),
        options: {
          customBodyRender: scoreRender(ratingTypes.SENTIMENT, 'social'),
          display: ratingType === ratingTypes.SENTIMENT,
        },
      },
      {
        name: 'esgGovernance',
        label: t('PORTFOLIO.GOVERNANCE'),
        options: {
          customBodyRender: scoreRender(ratingTypes.SENTIMENT, 'governance'),
          display: ratingType === ratingTypes.SENTIMENT,
        },
      },
      {
        name: 'esgTotal',
        label: t('Total'),
        options: {
          customBodyRender: scoreRender(ratingTypes.SENTIMENT),
          display: ratingType === ratingTypes.SENTIMENT,
        },
      },
      {
        name: 'climateLoss',
        label: t('PORTFOLIO.CLIMATE_LOSS'),
        options: {
          customBodyRender: scoreRender(
            ratingTypes.CLIMATE_STRESS_TEST,
            {
              orderly: 'climateLossOrderly',
              disorderly: 'climateLossDisorderly',
              hotHouse: 'climateLossHotHouse',
            }[cstScenario] as SubScore,
          ),
          customHeadLabelRender: cstTooltipRenderer,
          display: ratingType === ratingTypes.CLIMATE_STRESS_TEST,
        },
      },
      {
        name: 'physicalRiskLoss',
        label: t('PORTFOLIO.PHYSICAL_RISK_LOSS'),
        options: {
          customBodyRender: scoreRender(
            ratingTypes.CLIMATE_STRESS_TEST,
            {
              orderly: 'physicalRiskLossOrderly',
              disorderly: 'physicalRiskLossDisorderly',
              hotHouse: 'physicalRiskLossHotHouse',
            }[cstScenario] as SubScore,
          ),
          customHeadLabelRender: cstTooltipRenderer,
          display: ratingType === ratingTypes.CLIMATE_STRESS_TEST,
        },
      },
      {
        name: 'transitionRiskLoss',
        label: t('PORTFOLIO.TRANSITION_RISK_LOSS'),
        options: {
          customBodyRender: scoreRender(
            ratingTypes.CLIMATE_STRESS_TEST,
            {
              orderly: 'transitionRiskLossOrderly',
              disorderly: 'transitionRiskLossDisorderly',
              hotHouse: 'transitionRiskLossHotHouse',
            }[cstScenario] as SubScore,
          ),
          customHeadLabelRender: cstTooltipRenderer,
          display: ratingType === ratingTypes.CLIMATE_STRESS_TEST,
        },
      },
      {
        name: 'cr_activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          sort: true,
          customBodyRender: activityRender(ratingTypes.CREDIT_RATING),
          display: ratingType === ratingTypes.CREDIT_RATING && features.activityInPortfolioManager,
        },
      },
      {
        name: 'sig_activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          sort: true,
          customBodyRender: activityRender(ratingTypes.CREDIT_REVIEW),
          display:
            !isRequestHistory &&
            ratingType === ratingTypes.CREDIT_REVIEW &&
            features.activityInPortfolioManager,
        },
      },
      {
        name: 'rsr_activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          sort: true,
          customBodyRender: activityRender(ratingTypes.CREDIT_REVIEW),
          display: isRSRPortfolio,
        },
      },
      {
        name: 'esg_activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          sort: true,
          customBodyRender: activityRender(ratingTypes.ESG_IMPACT),
          display: ratingType === ratingTypes.ESG_IMPACT && features.activityInPortfolioManager,
        },
      },
      {
        name: 'sentiment_activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          sort: true,
          customBodyRender: activityRender(ratingTypes.SENTIMENT),
          display: !isRequestHistory && ratingType === ratingTypes.SENTIMENT,
        },
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ratingType, singleSelect, multiSelect, currentSelect, cstScenario],
  );

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (items: SelectedEntities) => [
    {
      text: 'Copy to portfolio',
      handler: copyEntity,
      hidden: items.count === 0,
      disabled: portfolioType === PortfolioType.SAMPLE_PORTFOLIO,
    },
    {
      text: 'Move to portfolio',
      handler: moveEntity,
      hidden: items.count === 0,
      disabled: isDedicated,
    },
    {
      text: 'Request selected',
      handler: handleRequestSelected,
      tooltip:
        items.count > maxRequestCount || selectedAll
          ? `${t('PORTFOLIO.YOU_CAN_NOT_REQUEST_MORE_THAN')} ${maxRequestCount} ${t(
              'PORTFOLIO.ITEMS_AT_ONCE',
            )}`
          : '',
      hidden:
        !features.multipleRequestsPortfolioManager ||
        !ratingType ||
        items.count === 0 ||
        ratingType === ratingTypes.CREDIT_REVIEW ||
        ratingType === ratingTypes.SENTIMENT,
      disabled: items.count > maxRequestCount || selectedAll || isDedicated,
    },
    {
      text: 'Export to Excel',
      handler: exportToExcel,
      hidden:
        items.count === 0 ||
        ratingType === ratingTypes.SENTIMENT ||
        ratingType === ratingTypes.ESG_IMPACT ||
        ratingType === ratingTypes.CLIMATE_STRESS_TEST,
    },
    {
      text: 'Export View',
      handler: exportToExcel,
      hidden:
        items.count === 0 ||
        (ratingType !== ratingTypes.ESG_IMPACT && ratingType !== ratingTypes.CLIMATE_STRESS_TEST),
    },
    {
      text: 'Export Full Data',
      handler: exportFullDataToExcel,
      hidden:
        items.count === 0 ||
        (ratingType !== ratingTypes.ESG_IMPACT && ratingType !== ratingTypes.CLIMATE_STRESS_TEST),
    },
    {
      text: 'Create companies',
      handler: () => {
        createCompanies.show();
        clearSingle();
      },
      hidden:
        (items.count === 1 && !Object.keys(items.items).length) ||
        isDedicated ||
        ratingType === ratingTypes.SENTIMENT,
    },
    {
      text: 'Delete',
      handler: deleteEntity,
      hidden: items.count === 0,
      color: theme.palette.error.main,
      disabled: isDedicated,
      confirmation: {
        title: 'Deleting entity',
        text: 'Do you really want to delete that entity?',
      },
    },
  ];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */
  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const getSortColumn = (column: string) => {
    const isCST =
      column === 'climateLoss' || column === 'physicalRiskLoss' || column === 'transitionRiskLoss';

    return isCST ? `${cstScenario}.${column}` : column;
  };

  const { data, loading, refetch } = useFetchPortfolioItems(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: getSortColumn(pagination.sort.sortColumn),
        direction: pagination.sort.direction,
      },
    ],
    requestedTab(),
    filtersData,
  );

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    if (refetchPortfolio) {
      refetchPortfolio();
    }
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  const { portfolioItems, totalPortfolioItems: filteredEntitiesCount } = data || {
    portfolioItems: [],
    totalPortfolioItems: 0,
  };

  const tableData = useMemo(() => makePortfolioItemsTableData(portfolioItems), [portfolioItems]);

  const showTabsButtons =
    !!portfolioType &&
    ![
      PortfolioType.REFERENCE_PORTFOLIO_EUROPE,
      PortfolioType.REFERENCE_PORTFOLIO_GLOBAL,
      PortfolioType.RSR_PORTFOLIO,
      PortfolioType.PRIVATE_RATING_PORTFOLIO,
      PortfolioType.ON_DEMAND_RATING_PORTFOLIO,
    ].includes(portfolioType);

  useEffect(() => {
    refreshList(true, 'filtersUpdate');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersData]);

  useEffect(() => {
    if (totalCounter) {
      totalCounter(filteredEntitiesCount);
    }
  }, [portfolioItems]);

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={filteredEntitiesCount || total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        tableToolbar={
          ratingType === ratingTypes.CLIMATE_STRESS_TEST && (
            <Select
              size="small"
              className={classes.cstSelect}
              value={cstScenario}
              onChange={(e) => setCstScenario(e.target.value)}
            >
              <MenuItem key="orderly" value="orderly">
                {t('Orderly')}
              </MenuItem>
              <MenuItem key="disorderly" value="disorderly">
                {t('Disorderly')}
              </MenuItem>
              <MenuItem key="hotHouse" value="hotHouse">
                {t('Hot House')}
              </MenuItem>
            </Select>
          )
        }
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        removeShadow
        popoverFilters={features.popoverFiltersPortfolioManager ? entitiesFilters : <></>}
        filterChips={filterChips}
        leftToolbar={showTabsButtons && tabsButtons}
        showSelectionExtension
        requestCounter={
          (ratingType === ratingTypes.CREDIT_REVIEW && availableRequestsRender) ||
          (ratingType === ratingTypes.SENTIMENT && sentimentRequestsRender) ||
          undefined
        }
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        onActionSuccess={refreshList}
        itemsData={currentSelect}
        sourcePortfolioId={portfolioId}
        variant={variant}
        itemType="ENTITY"
      />
      <RequestEsgScoreModal
        open={requestEsgScore.isOpen}
        onClose={() => {
          requestEsgScore.close();
          setActiveItem(undefined);
        }}
        onRequested={refreshList}
        selectIds={prepareRequestIds()}
      />
      <ESGScoringToolModal
        open={createCompanies.isOpen}
        onClose={createCompanies.close}
        onUploaded={refreshList}
        isPortfolioManager
        portfolioId={portfolioId}
        selectedIds={Object.values(currentSelect.items).map((item) => (item as Array<any>)[0][1])}
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={currentSelect}
        itemType="ENTITY"
        actionVariant={variant}
        sourcePortfolioId={portfolioId}
      />
      <CreditReviewRequestConfirmationModal
        open={requestConfirmationModalOpen}
        onClose={toggleRequestConfirmationModal}
        requestsCount={entitiesToRequestCount}
      />
    </Box>
  );
};
